import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Trash2, ShieldCheck, User as UserIcon, Clock, Mail, ExternalLink, Save } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import CustomFieldsManager from "@/components/CustomFieldsManager";

export default function Settings() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const r = await api.get("/users");
      setUsers(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load users");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (uid, status) => {
    try {
      await api.patch(`/users/${uid}/status`, { status });
      toast.success(`User ${status}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const setRole = async (uid, role) => {
    try {
      await api.patch(`/users/${uid}/role`, { role });
      toast.success(`Role set to ${role}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const removeUser = async (uid) => {
    if (!window.confirm("Delete this user? Their sessions will be revoked.")) return;
    try {
      await api.delete(`/users/${uid}`);
      toast.success("User deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const pending = users.filter((u) => u.status === "pending");
  const approved = users.filter((u) => u.status === "approved");
  const rejected = users.filter((u) => u.status === "rejected");

  if (loading) return <div className="p-8 overline">Loading…</div>;

  return (
    <div className="p-8 space-y-8" data-testid="settings-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="overline mb-2"><span className="inline-block w-6 h-[2px] bg-[#16A34A] mr-2 align-middle" />Administration</div>
          <h1 className="font-display text-4xl uppercase text-zinc-900">Settings · Users</h1>
          <div className="font-mono text-xs text-zinc-500 mt-2">
            {pending.length} pending · {approved.length} approved · {rejected.length} rejected
          </div>
        </div>
      </div>

      <section data-testid="pending-section">
        <div className="flex items-center gap-2 mb-3">
          <Clock className="w-4 h-4 text-[#D97706]" />
          <div className="font-display text-lg uppercase text-zinc-900">Pending Approval</div>
          {pending.length > 0 && <span className="badge-warn">{pending.length}</span>}
        </div>
        <div className="border border-zinc-200 bg-white">
          {pending.length === 0 ? (
            <div className="p-8 text-center text-zinc-500 font-sans">
              <CheckCircle2 className="w-10 h-10 mx-auto mb-3 opacity-40 text-[#16A34A]" />
              No pending requests.
            </div>
          ) : (
            <ul className="divide-y divide-zinc-200">
              {pending.map((u) => (
                <li key={u.user_id} className="p-4 flex flex-wrap items-center gap-4" data-testid={`pending-${u.user_id}`}>
                  {u.picture ? <img src={u.picture} alt="" className="w-10 h-10 rounded-full border border-zinc-200" /> : <div className="w-10 h-10 rounded-full bg-zinc-200 flex items-center justify-center"><UserIcon className="w-5 h-5 text-zinc-400" /></div>}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-zinc-900 truncate">{u.name}</div>
                    <div className="text-xs text-zinc-500 truncate flex items-center gap-1"><Mail className="w-3 h-3" />{u.email}</div>
                  </div>
                  <select
                    value={u.role}
                    onChange={(e) => setRole(u.user_id, e.target.value)}
                    className="input-field w-32"
                    data-testid={`pending-role-${u.user_id}`}
                  >
                    <option value="staff">Staff</option>
                    <option value="admin">Admin</option>
                  </select>
                  <button onClick={() => setStatus(u.user_id, "approved")} className="btn-primary flex items-center gap-2" data-testid={`approve-${u.user_id}`}>
                    <CheckCircle2 className="w-4 h-4" /> Approve
                  </button>
                  <button onClick={() => setStatus(u.user_id, "rejected")} className="btn-ghost flex items-center gap-2" data-testid={`reject-${u.user_id}`}>
                    <XCircle className="w-4 h-4" /> Reject
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <section data-testid="approved-section">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck className="w-4 h-4 text-[#16A34A]" />
          <div className="font-display text-lg uppercase text-zinc-900">Approved Users</div>
        </div>
        <div className="border border-zinc-200 bg-white overflow-x-auto">
          <table className="data-table" data-testid="approved-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {approved.map((u) => {
                const isSelf = u.user_id === me?.user_id;
                const isOwner = u.email === "greenrgypower@gmail.com";
                return (
                  <tr key={u.user_id} data-testid={`user-${u.user_id}`}>
                    <td className="font-sans">
                      <div className="flex items-center gap-2">
                        {u.picture ? <img src={u.picture} alt="" className="w-7 h-7 rounded-full" /> : <div className="w-7 h-7 rounded-full bg-zinc-200" />}
                        <span className="font-semibold text-zinc-900">{u.name}</span>
                        {isOwner && <span className="badge-ok">Owner</span>}
                        {isSelf && !isOwner && <span className="badge-ok">You</span>}
                      </div>
                    </td>
                    <td className="font-sans">{u.email}</td>
                    <td>
                      <select
                        disabled={isOwner || isSelf}
                        value={u.role}
                        onChange={(e) => setRole(u.user_id, e.target.value)}
                        className="input-field w-28 py-1"
                        data-testid={`role-${u.user_id}`}
                      >
                        <option value="staff">Staff</option>
                        <option value="admin">Admin</option>
                      </select>
                    </td>
                    <td><span className="badge-ok">Approved</span></td>
                    <td>
                      {!isOwner && !isSelf && (
                        <div className="flex items-center gap-2">
                          <button onClick={() => setStatus(u.user_id, "rejected")} className="text-zinc-400 hover:text-[#D97706] transition-colors" title="Revoke access" data-testid={`revoke-${u.user_id}`}>
                            <XCircle className="w-4 h-4" />
                          </button>
                          <button onClick={() => removeUser(u.user_id)} className="text-zinc-400 hover:text-[#DC2626] transition-colors" title="Delete user" data-testid={`del-user-${u.user_id}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {rejected.length > 0 && (
        <section data-testid="rejected-section">
          <div className="flex items-center gap-2 mb-3">
            <XCircle className="w-4 h-4 text-[#DC2626]" />
            <div className="font-display text-lg uppercase text-zinc-900">Rejected</div>
          </div>
          <div className="border border-zinc-200 bg-white">
            <ul className="divide-y divide-zinc-200">
              {rejected.map((u) => (
                <li key={u.user_id} className="p-4 flex items-center gap-4" data-testid={`rejected-${u.user_id}`}>
                  {u.picture ? <img src={u.picture} alt="" className="w-9 h-9 rounded-full border border-zinc-200" /> : <div className="w-9 h-9 rounded-full bg-zinc-200" />}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-zinc-900 truncate">{u.name}</div>
                    <div className="text-xs text-zinc-500 truncate">{u.email}</div>
                  </div>
                  <span className="badge-danger">Rejected</span>
                  <button onClick={() => setStatus(u.user_id, "approved")} className="btn-ghost flex items-center gap-2" data-testid={`reinstate-${u.user_id}`}>
                    <CheckCircle2 className="w-4 h-4" /> Reinstate
                  </button>
                  <button onClick={() => removeUser(u.user_id)} className="text-zinc-400 hover:text-[#DC2626] transition-colors" data-testid={`del-rejected-${u.user_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      <CustomLinkSetting />
      <CustomFieldsManager />
    </div>
  );
}

function CustomLinkSetting() {
  const [link, setLink] = useState({ label: "", url: "" });
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/settings/custom-link")
      .then((r) => setLink(r.data || { label: "", url: "" }))
      .finally(() => setLoaded(true));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch("/settings/custom-link", link);
      setLink(r.data);
      toast.success("Custom link saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    setSaving(true);
    try {
      await api.patch("/settings/custom-link", { label: "", url: "" });
      setLink({ label: "", url: "" });
      toast.success("Custom link removed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return null;

  return (
    <section data-testid="custom-link-section">
      <div className="flex items-center gap-2 mb-3">
        <ExternalLink className="w-4 h-4 text-[#16A34A]" />
        <div className="font-display text-lg uppercase text-zinc-900">Custom Header Button</div>
      </div>
      <div className="border border-zinc-200 bg-white p-6 space-y-4">
        <div className="text-xs text-zinc-500 font-sans">
          Add a single quick-access button that appears in the top header for everyone. Leave both fields empty to hide it.
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="block">
            <div className="overline mb-2">Button Label</div>
            <input
              value={link.label}
              onChange={(e) => setLink({ ...link, label: e.target.value })}
              placeholder="e.g. Company Portal"
              className="input-field w-full"
              data-testid="custom-link-label"
              maxLength={40}
            />
          </label>
          <label className="block">
            <div className="overline mb-2">URL</div>
            <input
              value={link.url}
              onChange={(e) => setLink({ ...link, url: e.target.value })}
              placeholder="https://example.com"
              className="input-field w-full font-mono"
              data-testid="custom-link-url"
            />
          </label>
        </div>
        {link.label && link.url && (
          <div className="text-xs text-zinc-500 font-sans">
            Preview: <a href={link.url} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#0B3022] text-white text-[11px] uppercase font-bold tracking-widest rounded ml-2" data-testid="custom-link-preview">
              <ExternalLink className="w-3 h-3" />{link.label}
            </a>
          </div>
        )}
        <div className="flex gap-3 pt-2">
          <button onClick={save} disabled={saving} className="btn-primary flex items-center gap-2" data-testid="save-custom-link">
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
          </button>
          {(link.label || link.url) && (
            <button onClick={clear} disabled={saving} className="btn-ghost flex items-center gap-2" data-testid="clear-custom-link">
              <XCircle className="w-4 h-4" /> Remove
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
